import React, { useMemo } from "react";
import useThemeMode from "../../hooks/useThemeMode";
import { getMarketingTheme } from "../../utils/marketingTheme";
import useI18n from "../../i18n/useI18n";

const HomeFeatures = () => {
  const { isDark } = useThemeMode();
  const { t } = useI18n();
  const colors = useMemo(() => getMarketingTheme(isDark), [isDark]);

  const tt = (key, fallback) => {
    const value = t(key);
    return value === key ? fallback : value;
  };

  const featureCards = useMemo(
    () => [
      {
        title: tt("homeFeatures.cards.createFaster.title", "Create faster"),
        text: tt(
          "homeFeatures.cards.createFaster.text",
          "Move from rough idea to usable content faster without getting stuck on wording."
        ),
      },
      {
        title: tt("homeFeatures.cards.keepMessageSharp.title", "Keep your message sharp"),
        text: tt(
          "homeFeatures.cards.keepMessageSharp.text",
          "Generate clearer hooks, titles, captions, and scripts that feel more intentional."
        ),
      },
      {
        title: tt(
          "homeFeatures.cards.upgradeOnlyWhenNeeded.title",
          "Upgrade only when needed"
        ),
        text: tt(
          "homeFeatures.cards.upgradeOnlyWhenNeeded.text",
          "Start with Free, then unlock deeper Creator and Pro tools as your workflow grows."
        ),
      },
    ],
    [t]
  );

  const toolCards = useMemo(
    () => [
      tt(
        "homeFeatures.toolCards.free",
        "Free tools for hooks, titles, captions, hashtags, descriptions, and scripts."
      ),
      tt(
        "homeFeatures.toolCards.creatorOne",
        "Creator tools for hook angles, CTAs, and stronger content packaging."
      ),
      tt(
        "homeFeatures.toolCards.creatorTwo",
        "Retention support, rewrites, and thumbnail text improvement."
      ),
      tt(
        "homeFeatures.toolCards.creatorThree",
        "Repurposing, ad copy, carousel writing, and offer positioning."
      ),
      tt(
        "homeFeatures.toolCards.creatorFour",
        "Hook score analysis, Nigerian rewrites, and comment-driven content ideas."
      ),
      tt(
        "homeFeatures.toolCards.proOne",
        "Pro tools for trend conversion and deeper creative strategy."
      ),
      tt(
        "homeFeatures.toolCards.proTwo",
        "Brand voice systems to make content feel more consistent."
      ),
      tt(
        "homeFeatures.toolCards.proThree",
        "Content differentiation tools to help creators stand out more clearly."
      ),
    ],
    [t]
  );

  return (
    <section style={{ padding: "20px 20px 72px", background: colors.sectionBg }}>
      <div style={{ maxWidth: "1200px", margin: "0 auto" }}>
        <div style={{ textAlign: "center", marginBottom: "32px" }}>
          <h2
            style={{
              margin: "0 0 12px",
              fontSize: "clamp(2rem, 4vw, 3rem)",
              color: colors.text,
              lineHeight: "1.08",
            }}
          >
            {tt("homeFeatures.title", "Why creators use Hookora")}
          </h2>
          <p
            style={{
              maxWidth: "820px",
              margin: "0 auto",
              color: colors.textMuted,
              fontSize: "16px",
              lineHeight: "1.7",
            }}
          >
            {tt(
              "homeFeatures.subtitle",
              "A cleaner workflow for hooks, titles, captions, scripts, and stronger content packaging."
            )}
          </p>
        </div>

        <div
          style={{
            display: "grid",
            gridTemplateColumns: "repeat(auto-fit, minmax(260px, 1fr))",
            gap: "18px",
            marginBottom: "44px",
          }}
        >
          {featureCards.map((feature) => (
            <div
              key={feature.title}
              style={{
                background: colors.cardBg,
                border: `1px solid ${colors.border}`,
                borderRadius: "22px",
                padding: "22px",
                boxShadow: colors.shadow,
                backdropFilter: isDark ? "blur(14px)" : "none",
              }}
            >
              <div
                style={{
                  width: "42px",
                  height: "42px",
                  borderRadius: "12px",
                  background: "linear-gradient(135deg, #2563eb, #7c3aed)",
                  marginBottom: "16px",
                }}
              />
              <h3 style={{ margin: "0 0 10px", color: colors.text, fontSize: "20px" }}>
                {feature.title}
              </h3>
              <p
                style={{
                  margin: 0,
                  color: colors.textMuted,
                  lineHeight: "1.7",
                  fontSize: "15px",
                }}
              >
                {feature.text}
              </p>
            </div>
          ))}
        </div>

        <div
          style={{
            background: colors.cardBg,
            border: `1px solid ${colors.border}`,
            borderRadius: "28px",
            padding: "28px",
            boxShadow: colors.shadow,
            backdropFilter: isDark ? "blur(14px)" : "none",
          }}
        >
          <div style={{ marginBottom: "22px" }}>
            <div
              style={{
                display: "inline-flex",
                padding: "7px 12px",
                borderRadius: "999px",
                background: isDark ? "rgba(109,40,217,0.18)" : "#f5f3ff",
                color: isDark ? "#d8b4fe" : "#6d28d9",
                fontWeight: "700",
                fontSize: "12px",
                marginBottom: "14px",
                border: isDark ? `1px solid ${colors.border}` : "none",
              }}
            >
              {tt("homeFeatures.studioToolsBadge", "Studio tools overview")}
            </div>

            <h3 style={{ margin: "0 0 10px", color: colors.text, fontSize: "28px" }}>
              {tt("homeFeatures.workflowTitle", "One workflow, multiple content tools")}
            </h3>

            <p
              style={{
                margin: 0,
                color: colors.textMuted,
                lineHeight: "1.7",
                fontSize: "15px",
              }}
            >
              {tt(
                "homeFeatures.workflowDescription",
                "Hookora connects your writing workflow so you can move from hooks to stronger titles, captions, scripts, rewrites, and more without switching between messy tools."
              )}
            </p>
          </div>

          <div
            style={{
              display: "grid",
              gridTemplateColumns: "repeat(auto-fit, minmax(220px, 1fr))",
              gap: "14px",
            }}
          >
            {toolCards.map((tool) => (
              <div
                key={tool}
                style={{
                  background: colors.cardBgSoft,
                  border: `1px solid ${colors.border}`,
                  borderRadius: "16px",
                  padding: "16px",
                  color: colors.text,
                  fontWeight: "700",
                  lineHeight: "1.5",
                }}
              >
                {tool}
              </div>
            ))}
          </div>
        </div>
      </div>
    </section>
  );
};

export default HomeFeatures;